/**
 * Componente principal da aplicação
 * 
 * @author Equipe GestaoeCRM
 * @date 25/09/2025
 */

import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { HeartbeatProvider } from './contexts/HeartbeatContext';
import { PIPProvider } from './contexts/PIPContext';

// Importar componentes essenciais (não lazy)
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';
import LoadingState from './components/LoadingState';
import PermissionGuard from './components/PermissionGuard';
import { RoleGuard } from './components/RoleGuard';
import { ErrorBoundary } from './components/ErrorBoundary';

// Lazy loading de páginas públicas
const LoginPage = lazy(() => import('./pages/LoginPage'));
const PlansPage = lazy(() => import('./pages/PlansPage'));
const PaymentConfirmationPage = lazy(() => import('./pages/PaymentConfirmationPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));

// Lazy loading de páginas protegidas
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ClientsPage = lazy(() => import('./pages/ClientsPage'));
const ClientDetailsPage = lazy(() => import('./pages/ClientDetailsPage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const DemandsPage = lazy(() => import('./pages/DemandsPage'));
const DemandDetailsPage = lazy(() => import('./pages/DemandDetailsPage'));
const ChecklistPage = lazy(() => import('./pages/ChecklistPage'));
const JobsTasksPage = lazy(() => import('./pages/JobsTasksPage'));
const HourRatesPage = lazy(() => import('./pages/HourRatesPage'));
const OrdersOfServicePage = lazy(() => import('./pages/OrdersOfServicePage'));
const OSDetailsPage = lazy(() => import('./pages/OSDetailsPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const OSByAreaReportPage = lazy(() => import('./pages/OSByAreaReportPage'));
const OSCompletedReportPage = lazy(() => import('./pages/OSCompletedReportPage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const AdministrationPage = lazy(() => import('./pages/AdministrationPage'));
const PermissionsManagementPage = lazy(() => import('./pages/PermissionsManagementPage'));
const TenantsPage = lazy(() => import('./pages/TenantsPage'));
const CompanyAreasPage = lazy(() => import('./pages/CompanyAreasPage'));
const AreaDetailsPage = lazy(() => import('./pages/AreaDetailsPage'));
const AreaTransferPage = lazy(() => import('./pages/AreaTransferPage'));
const AdminSpreadsheetPage = lazy(() => import('./pages/AdminSpreadsheetPage'));
const TestPaginationPage = lazy(() => import('./pages/TestPaginationPage'));
const SLAPage = lazy(() => import('./pages/SLAPage'));
const DailyScrumPage = lazy(() => import('./pages/DailyScrumPage'));
const NotificationHistoryPage = lazy(() => import('./pages/NotificationHistoryPage'));
const UsageAndLimitsPage = lazy(() => import('./pages/UsageAndLimitsPage'));
const MyPlanPage = lazy(() => import('./pages/MyPlanPage'));
const UpgradeRenewPage = lazy(() => import('./pages/UpgradeRenewPage'));
const GlobalDashboardPage = lazy(() => import('./pages/GlobalDashboardPage'));
const PlanManagementPage = lazy(() => import('./pages/PlanManagementPage'));
const TenantManagementPage = lazy(() => import('./pages/TenantManagementPage'));
const AdminReportsPage = lazy(() => import('./pages/AdminReportsPage'));
const AdvancedFeaturesPage = lazy(() => import('./pages/AdvancedFeaturesPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));

// Componente de fallback para Suspense
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background-dark/30">
    <LoadingState message="Carregando página..." size="lg" />
  </div>
);

// Função helper para determinar rota de redirecionamento baseada no role
const getDefaultRoute = (userRole?: string): string => {
  if (userRole === 'SUPER_ADMIN') {
    return '/global-dashboard';
  }
  return '/dashboard';
};

function App() {
  // console.log('🔍 DEBUG: App.tsx carregado');
  
  const { user, loading } = useAuth();
  
  // console.log('🔍 DEBUG: Auth state:', { user: !!user, loading, userEmail: user?.email });

  // Mostrar loading enquanto verifica autenticação
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Rotas públicas */}
      <Route 
        path="/login" 
        element={
          user ? (
            <Navigate to={getDefaultRoute(user.role)} replace />
          ) : (
            <Suspense fallback={<PageLoader />}>
              <LoginPage />
            </Suspense>
          )
        } 
      />
      <Route 
        path="/plans" 
        element={
          <Suspense fallback={<PageLoader />}>
            <PlansPage />
          </Suspense>
        } 
      />
      <Route 
        path="/payment/success" 
        element={
          <Suspense fallback={<PageLoader />}>
            <PaymentConfirmationPage />
          </Suspense>
        } 
      />
      <Route 
        path="/register" 
        element={
          <Suspense fallback={<PageLoader />}>
            <RegisterPage />
          </Suspense>
        } 
      />
      
      {/* Rotas protegidas */}
      <Route
        path="/*"
        element={
          user ? (
            <ErrorBoundary>
              <HeartbeatProvider>
                <PIPProvider>
                  <Layout>
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                        <Route path="/" element={<Navigate to={getDefaultRoute(user?.role)} replace />} />
                        <Route path="/dashboard" element={
                          <PermissionGuard menuKey="dashboard">
                            <DashboardPage />
                          </PermissionGuard>
                        } />
                        <Route path="/clients" element={
                          <PermissionGuard menuKey="clients">
                            <ClientsPage />
                          </PermissionGuard>
                        } />
                        <Route path="/clients/:id" element={
                          <PermissionGuard menuKey="clients">
                            <ClientDetailsPage />
                          </PermissionGuard>
                        } />
                        <Route path="/projects" element={
                          <PermissionGuard menuKey="projects">
                            <ProjectsPage />
                          </PermissionGuard>
                        } />
                        <Route path="/demands" element={
                          <PermissionGuard menuKey="demands">
                            <DemandsPage />
                          </PermissionGuard>
                        } />
                        <Route path="/demands/:id" element={
                          <PermissionGuard menuKey="demands">
                            <DemandDetailsPage />
                          </PermissionGuard>
                        } />
                        <Route path="/demands/:id/checklist" element={
                          <PermissionGuard menuKey="demands">
                            <ChecklistPage />
                          </PermissionGuard>
                        } />
                        <Route path="/demands/:id/jobs-tasks" element={
                          <PermissionGuard menuKey="demands">
                            <JobsTasksPage />
                          </PermissionGuard>
                        } />
                        <Route path="/hour-rates" element={
                          <PermissionGuard menuKey="hour-rates">
                            <HourRatesPage />
                          </PermissionGuard>
                        } />
                        <Route path="/administration/hour-rates" element={
                          <PermissionGuard menuKey="hour-rates">
                            <HourRatesPage />
                          </PermissionGuard>
                        } />
                        <Route path="/sla" element={
                          <PermissionGuard menuKey="sla">
                            <SLAPage />
                          </PermissionGuard>
                        } />
                        <Route path="/administration/sla" element={
                          <PermissionGuard menuKey="sla">
                            <SLAPage />
                          </PermissionGuard>
                        } />
                        <Route path="/daily-scrum" element={
                          <PermissionGuard menuKey="daily-scrum">
                            <DailyScrumPage />
                          </PermissionGuard>
                        } />
                        <Route path="/os" element={
                          <PermissionGuard menuKey="os">
                            <OrdersOfServicePage />
                          </PermissionGuard>
                        } />
                        <Route path="/os/:id" element={
                          <PermissionGuard menuKey="os">
                            <OSDetailsPage />
                          </PermissionGuard>
                        } />
                        <Route path="/reports" element={
                          <PermissionGuard menuKey="reports">
                            <ReportsPage />
                          </PermissionGuard>
                        } />
                        <Route path="/reports/os-by-area" element={
                          <PermissionGuard menuKey="reports">
                            <OSByAreaReportPage />
                          </PermissionGuard>
                        } />
                        <Route path="/reports/os-completed" element={
                          <PermissionGuard menuKey="reports">
                            <OSCompletedReportPage />
                          </PermissionGuard>
                        } />
                        <Route path="/users" element={
                          <PermissionGuard menuKey="users">
                            <UsersPage />
                          </PermissionGuard>
                        } />
                        <Route path="/administration/users" element={
                          <PermissionGuard menuKey="users">
                            <UsersPage />
                          </PermissionGuard>
                        } />
                        <Route path="/administration" element={
                          <PermissionGuard menuKey="administration">
                            <AdministrationPage />
                          </PermissionGuard>
                        } />
                        <Route path="/administration/permissions" element={
                          <PermissionGuard menuKey="permissions">
                            <PermissionsManagementPage />
                          </PermissionGuard>
                        } />
                        <Route path="/administration/tenants" element={
                          <PermissionGuard menuKey="tenants">
                            <TenantsPage />
                          </PermissionGuard>
                        } />
                        <Route path="/administration/company-areas" element={
                          <PermissionGuard menuKey="company-areas">
                            <CompanyAreasPage />
                          </PermissionGuard>
                        } />
                        <Route path="/administration/company-areas/:id" element={
                          <PermissionGuard menuKey="company-areas">
                            <AreaDetailsPage />
                          </PermissionGuard>
                        } />
                        <Route path="/administration/company-areas/transfer" element={
                          <PermissionGuard menuKey="company-areas">
                            <AreaTransferPage />
                          </PermissionGuard>
                        } />
                        <Route path="/administration/spreadsheet" element={
                          <PermissionGuard menuKey="admin-spreadsheet">
                            <AdminSpreadsheetPage />
                          </PermissionGuard>
                        } />
                        <Route path="/test-pagination" element={
                          <TestPaginationPage />
                        } />
                        <Route path="/notifications" element={
                          <NotificationHistoryPage />
                        } />
                        <Route path="/usage-and-limits" element={
                          <PermissionGuard menuKey="administration">
                            <UsageAndLimitsPage />
                          </PermissionGuard>
                        } />
                        <Route path="/my-plan" element={
                          <PermissionGuard menuKey="administration">
                            <MyPlanPage />
                          </PermissionGuard>
                        } />
                        <Route path="/administration/upgrade-renew" element={
                          <PermissionGuard menuKey="upgrade-renew">
                            <UpgradeRenewPage />
                          </PermissionGuard>
                        } />
                        <Route path="/global-dashboard" element={
                          <RoleGuard allowedRoles={['SUPER_ADMIN']}>
                            <GlobalDashboardPage />
                          </RoleGuard>
                        } />
                        <Route path="/plan-management" element={
                          <RoleGuard allowedRoles={['SUPER_ADMIN']}>
                            <PlanManagementPage />
                          </RoleGuard>
                        } />
                        <Route path="/tenant-management" element={
                          <RoleGuard allowedRoles={['SUPER_ADMIN']}>
                            <TenantsPage />
                          </RoleGuard>
                        } />
                        <Route path="/tenant-management/:id" element={
                          <RoleGuard allowedRoles={['SUPER_ADMIN']}>
                            <TenantManagementPage />
                          </RoleGuard>
                        } />
                        <Route path="/admin-reports" element={
                          <RoleGuard allowedRoles={['SUPER_ADMIN']}>
                            <AdminReportsPage />
                          </RoleGuard>
                        } />
                        <Route path="/advanced-features" element={
                          <RoleGuard allowedRoles={['SUPER_ADMIN']}>
                            <AdvancedFeaturesPage />
                          </RoleGuard>
                        } />
                        <Route path="/onboarding" element={
                          <RoleGuard allowedRoles={['ADMIN', 'GESTOR']}>
                            <OnboardingPage />
                          </RoleGuard>
                        } />
                        <Route path="*" element={<Navigate to={getDefaultRoute(user?.role)} replace />} />
                      </Routes>
                    </Suspense>
                  </Layout>
                </PIPProvider>
              </HeartbeatProvider>
            </ErrorBoundary>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
    </Routes>
  );
}

export default App;
